import { camelCase } from '@replygirl/change-case-object';
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useToast } from '@/composables/useToast';
import { Api } from '@/Api';
import type { PusherPlugin } from '@/plugins/pusher';

type AgentStatus = 'processing' | 'ready' | 'failed';

const AGENT_UPDATE_EVENT = 'agent_update';

export const useAgentActions = () => {
  const viewerItemStore = useViewerItemStore();
  const { error: toastError } = useToast();
  const pusher = inject<PusherPlugin>('$pusher');

  const isSubmittingCreate = ref(false);
  const existingAgentId = ref<string | null>(null);
  const existingAgentStatus = ref<AgentStatus | null>(null);

  const isAgentProcessing = computed(() => existingAgentStatus.value === 'processing');
  const isCreatingAgent = computed(() => isSubmittingCreate.value || isAgentProcessing.value);

  const agentButtonLabel = computed(() => {
    if (isAgentProcessing.value) {
      return 'Playbook is being created...';
    }
    if (isSubmittingCreate.value) {
      return 'Creating...';
    }
    if (existingAgentId.value) {
      return 'Recreate Playbook';
    }
    return 'Create Playbook';
  });

  const applyAgent = (match?: { id?: string; attributes?: { status?: AgentStatus }; status?: AgentStatus }) => {
    if (!match) {
      existingAgentId.value = null;
      existingAgentStatus.value = null;
      return;
    }
    existingAgentId.value = match.id || null;
    existingAgentStatus.value = (match.attributes?.status || match.status || null) as AgentStatus | null;
  };

  // Pusher events bypass Api.ts so attributes arrive snake_case; normalize
  // before delegating to applyAgent (which reads camelCase keys).
  const handleAgentUpdate = (payload: unknown) => {
    const data = camelCase(payload as Record<string, unknown>) as {
      data?: { agent?: { id?: string; attributes?: { status?: AgentStatus } } };
    };
    const agent = data?.data?.agent;
    if (!agent || !existingAgentId.value || agent.id !== existingAgentId.value) {
      return;
    }
    applyAgent(agent);
  };

  const refreshExistingAgent = async () => {
    const item = viewerItemStore.item;
    if (!item) {
      return;
    }
    try {
      const response = await Api.agents.list({ itemId: item.id });
      const agents = response?.data?.agents || [];
      const match = agents.find((a: { id?: string; relationships?: { item?: { data?: { id?: string } } } }) => a?.relationships?.item?.data?.id === item.id);
      applyAgent(match);
    } catch {
      // Not critical — just won't show "Recreate" label or processing state.
    }
  };

  onMounted(() => {
    refreshExistingAgent();
    pusher?.bindUser(AGENT_UPDATE_EVENT, handleAgentUpdate);
  });

  onBeforeUnmount(() => {
    pusher?.unbindUser(AGENT_UPDATE_EVENT, handleAgentUpdate);
  });

  const createAgentAction = async () => {
    const item = viewerItemStore.item;
    if (!item) {
      return;
    }
    if (isCreatingAgent.value) {
      return;
    }

    isSubmittingCreate.value = true;
    try {
      const itemName = item.attributes?.name || item.attributes?.nameWithoutExtension || 'Untitled Playbook';
      const response = await Api.agents.create({
        itemId: item.id,
        title: `Playbook: ${itemName}`,
      });
      applyAgent(response?.data?.agent);
    } catch (e: unknown) {
      const errorObj = e as { errors?: Array<{ detail?: string }>; message?: string };
      const errorMsg = errorObj?.errors?.[0]?.detail || errorObj?.message || 'Failed to create playbook';
      toastError(errorMsg);
    } finally {
      isSubmittingCreate.value = false;
    }
  };

  const viewAgent = () => {
    if (existingAgentId.value) {
      window.location.href = `/dashboard-new/agents?view=${existingAgentId.value}`;
    }
  };

  return {
    isCreatingAgent,
    existingAgentId,
    agentButtonLabel,
    createAgentAction,
    viewAgent,
  };
};
